/**
 * useComplianceBrief — derives the editorial copy block at the top of the
 * Baseline Compliance page from the active drift / score state.
 *
 * Mirrors `usePostureBrief`: memoises on value-shape (not object identity)
 * and delegates all copy composition to the pure `composeComplianceBrief`.
 * The byline is the provenance line (D36), so the signed-in user no longer
 * enters the brief.
 */

import { useMemo } from 'react'
import {
  composeComplianceBrief,
  type ComplianceBrief,
  type ComplianceNoData,
  type Pillar,
} from '@/lib/compliance-brief'
import type { ScanScheduleState } from '@/lib/brief-helpers'

export interface UseComplianceBriefArgs {
  /**
   * Active tenant ID. Kept for call-site compatibility; the composer is
   * deterministic (same input, same brief) and no longer varies copy per tenant.
   */
  tenantId?: string
  /** Compliance pillar score (0-100). */
  score?: number | null
  /** 30-day score delta — drives the delta tag, the verdict and the headline. */
  deltas?: { score?: number; days?: number }
  /** Highest-impact priority drift / action — informs the headline, lede, verdict and primary action. */
  topAction?: { pillar: Pillar; title: string; setting?: string; impact?: 'crit' | 'high' | 'med' }
  /** Open critical-severity drift count — drives the headline and verdict. */
  criticalCount?: number
  /**
   * Canonical workload names with active drifts (Identity / Exchange /
   * Teams / SharePoint / Defender). Map M365DSC component prefixes via
   * `getWorkloadForDriftComponent` before passing — the brief expects
   * workload buckets, not resource names or M365DSC resource codes.
   */
  driftingWorkloads?: string[]
  /**
   * Resources with active drift for the action-first lede (blueprint §6.2
   * row 2): `dashboardStats.activeDrifts` (data-map C10). Pass `undefined`
   * while the stats query loads, never 0; the lede then falls back to the
   * drifting workloads and the score.
   */
  driftedResources?: number
  /** Where the primary button goes (the drift table anchor); adds `primaryAction`. */
  primaryHref?: string
  /** Why the score is unknown (`baselineUnknownReason`); with a null score the
   *  brief says what to do next instead of reading as loading. */
  noData?: ComplianceNoData
  /** Ad-Hoc Scans, for a Platform Admin only: the unscanned brief's one action. */
  firstScanHref?: string
  /** Unscanned: Tenant Management, only for a viewer who may edit the scan schedule. */
  scheduleSettingsHref?: string
  /** Unscanned: the tenant's scan schedule (`useTenantScanSchedule`). */
  scanSchedule?: ScanScheduleState
  /** The tenant's last real assessment (`lastAssessmentAt`), for the provenance line. */
  assessedAt?: string | null
  /** Resources the baseline checks (`complianceScore.totalChecks`), the provenance scope. */
  resourcesChecked?: number
}

export function useComplianceBrief(args: UseComplianceBriefArgs = {}): ComplianceBrief {
  // Memoise on value-shape rather than object identity so the page can
  // rebuild `args` each render without retriggering composition.
  const deltaScore = args.deltas?.score
  const deltaDays = args.deltas?.days
  const topActionPillar = args.topAction?.pillar
  const topActionTitle = args.topAction?.title
  const topActionSetting = args.topAction?.setting
  const topActionImpact = args.topAction?.impact

  // Workload list — stringify for stable memo deps. The list is small
  // (single-digit) so the cost is negligible compared to the full brief
  // recomposition we'd otherwise trigger on every parent re-render.
  const workloadsKey = (args.driftingWorkloads ?? []).join('|')

  return useMemo(
    () =>
      composeComplianceBrief({
        tenantId: args.tenantId,
        score: args.score,
        deltas:
          deltaScore !== undefined || deltaDays !== undefined
            ? { score: deltaScore, days: deltaDays }
            : undefined,
        topAction:
          topActionPillar && topActionTitle
            ? {
                pillar: topActionPillar,
                title: topActionTitle,
                setting: topActionSetting,
                impact: topActionImpact,
              }
            : undefined,
        criticalCount: args.criticalCount,
        driftingWorkloads: args.driftingWorkloads,
        driftedResources: args.driftedResources,
        primaryHref: args.primaryHref,
        noData: args.noData,
        firstScanHref: args.firstScanHref,
        scheduleSettingsHref: args.scheduleSettingsHref,
        scanSchedule: args.scanSchedule,
        assessedAt: args.assessedAt,
        resourcesChecked: args.resourcesChecked,
      }),
    [
      args.tenantId,
      args.score,
      deltaScore,
      deltaDays,
      topActionPillar,
      topActionTitle,
      topActionSetting,
      topActionImpact,
      args.criticalCount,
      args.driftingWorkloads,
      workloadsKey,
      args.driftedResources,
      args.primaryHref,
      args.noData,
      args.firstScanHref,
      args.scheduleSettingsHref,
      args.scanSchedule,
      args.assessedAt,
      args.resourcesChecked,
    ],
  )
}
